/**
 * FooterDropDown component renders a dropdown menu with compliance notices
 * displayed in an accordion format. The component is conditionally rendered
 * based on the user's country and styled using a theme.
 *
 * @returns {JSX.Element | null} The rendered FooterDropDown component or null
 * if the user's country is not 'au'.
 *
 * @remarks
 * - The component uses `useMemo` to memoize the list of accordion items for
 *   performance optimization.
 * - The `globals?.country` is checked to ensure the component is only rendered
 *   for users in Australia ('au').
 * - The accordion styles are dynamically generated using the `useTheme` hook
 *   and `getAccordionStyles` function.
 *
 * @example
 * ```tsx
 * <FooterDropDown />
 * ```
 */

import { Accordion } from '@mantine/core';
import { globals } from 'common-ui';
import { useMemo } from 'react';
import { complianceNotices } from './tabContent';
import { css, useTheme } from '@emotion/react';
import { Logo } from './Logo';

interface AccordionLabelProps {
  title: string;
}

export function AccordionLabel({ title }: Readonly<AccordionLabelProps>): React.JSX.Element {
  return <div>{title}</div>;
}

export function FooterDropDown(): React.JSX.Element | null {
  const theme = useTheme();

  const items = useMemo(
    () =>
      complianceNotices.map(item => (
        <Accordion.Item value={item.id} key={item.title}>
          <Accordion.Control>
            <AccordionLabel title={item.title} />
          </Accordion.Control>
          <Accordion.Panel>
            {item.content} {item.mailToBody && <a href={item.mailToBody}>{item.mailToLabel}</a>}
            {item.image && <Logo image={item.image} alt="Drink Wise" />}
          </Accordion.Panel>
        </Accordion.Item>
      )),
    [],
  );

  if (globals?.country !== 'au') {
    return null;
  }

  const accordionStyles = css`
    ${theme.widgets.FooterDropDown?.main}
  `;

  return (
    <div css={accordionStyles}>
      <Accordion chevronPosition="right" variant="contained">
        {items}
      </Accordion>
    </div>
  );
}
